import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useCreateWorkspaceMutation, useSwitchTenantMutation, useGetWorkspaceStatusQuery } from '../features/auth/authApi';
import { useAppDispatch } from '../app/hooks';
import { api } from '../services/api';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface CreateWorkspaceModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const CreateWorkspaceModal: React.FC<CreateWorkspaceModalProps> = ({ isOpen, onClose }) => {
    const [companyName, setCompanyName] = useState('');
    const [slug, setSlug] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [jobId, setJobId] = useState<string | null>(null);
    const [isProvisioning, setIsProvisioning] = useState(false);

    const [createWorkspace, { isLoading: isCreating }] = useCreateWorkspaceMutation();
    const [switchTenant, { isLoading: isSwitching }] = useSwitchTenantMutation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const hasTriedSwitch = useRef(false);

    // Call status query with 2 second polling when jobId is active
    const { data: statusData } = useGetWorkspaceStatusQuery(jobId || '', {
        skip: !jobId || hasTriedSwitch.current,
        pollingInterval: 2000,
    });

    // Reset local state when opening/closing
    useEffect(() => {
        if (isOpen) {
            setCompanyName('');
            setSlug('');
            setCountryCode('');
            setJobId(null);
            setIsProvisioning(false);
            hasTriedSwitch.current = false;
        }
    }, [isOpen]);

    // Handle completed/failed background status
    useEffect(() => {
        if (!jobId || !statusData) return;

        const jobStatus = statusData.data?.status;

        if (jobStatus === 'completed' && !hasTriedSwitch.current) {
            hasTriedSwitch.current = true;
            const executeSwitch = async () => {
                try {
                    await switchTenant({ tenant_slug: slug }).unwrap();
                    toast.success('Workspace created and activated successfully!');
                    // Same reason as WorkspaceSwitcher's handleSwitch - without this,
                    // tenant-agnostic queries (permissions, roles, etc.) keep serving
                    // whatever tenant they were first fetched under.
                    dispatch(api.util.resetApiState());
                    setJobId(null);
                    setIsProvisioning(false);
                    onClose();
                    navigate('/dashboard');
                } catch (err: any) {
                    toast.error('Workspace created but auto-switch failed. Please refresh the page.');
                    setJobId(null);
                    setIsProvisioning(false);
                }
            };
            executeSwitch();
        } else if (jobStatus === 'failed' && !hasTriedSwitch.current) {
            hasTriedSwitch.current = true;
            const reason = statusData.data?.failedReason || 'An error occurred during database migration.';
            toast.error(`Provisioning Failed: ${reason}`);
            setJobId(null);
            setIsProvisioning(false);
        }
    }, [statusData, jobId, slug, switchTenant, navigate, onClose]);

    if (!isOpen) return null;

    const parseSlugInput = (value: string): string => {
        let val = value.toLowerCase();
        // Strip protocol
        val = val.replace(/https?:\/\//g, '');
        // Strip typical hostnames/domains
        val = val.replace(/(localhost:\d+|localhost|noorhr\.com|noorhr\.com\/|noorhr\/)/g, '');
        // Strip leading and trailing slashes
        val = val.replace(/(^\/|\/$)/g, '');
        // Clean any remaining non-alphanumeric chars
        return val.replace(/[^a-z0-9-]+/g, '');
    };

    const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.value;
        setCompanyName(name);
        setSlug(parseSlugInput(name).replace(/(^-|-$)+/g, ''));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!companyName || !slug || !countryCode) return;

        try {
            // 1. Create the workspace in the background (HTTP 202)
            const response = await createWorkspace({ company_name: companyName, slug, country_code: countryCode }).unwrap();
            const responseJobId = response.data?.job_id;

            if (responseJobId) {
                setJobId(responseJobId);
                setIsProvisioning(true);
            } else {
                // Fallback if no jobId is returned (should not happen)
                toast.success('Workspace setup initialized.');
                onClose();
            }
        } catch (error: any) {
            toast.error(error.data?.error || 'Failed to initiate workspace. Please try again.');
        }
    };

    const getStatusStepInfo = () => {
        const jobStatus = statusData?.data?.status || 'pending';
        switch (jobStatus) {
            case 'pending':
                return {
                    title: 'Queueing Workspace...',
                    description: 'Your request is in queue. Preparing worker environment...',
                    progress: 25,
                };
            case 'active':
                return {
                    title: 'Provisioning Database Schema...',
                    description: 'Generating isolated tables, indices, and structural migrations...',
                    progress: 60,
                };
            case 'completed':
                return {
                    title: 'Seeding Base Data...',
                    description: 'Populating base roles, permissions, and initial user context...',
                    progress: 90,
                };
            default:
                return {
                    title: 'Initializing...',
                    description: 'Setting up background worker thread...',
                    progress: 10,
                };
        }
    };

    // Rendered via a portal straight onto document.body - this component is
    // mounted deep inside the Sidebar (via WorkspaceSwitcher), whose root
    // element carries Tailwind's `transform` utility for its slide-in/collapse
    // animation. ANY ancestor with a transform (even an identity translate-x-0)
    // creates a new containing block for `position: fixed` descendants, so
    // without the portal this modal's "fixed inset-0" resolved against the
    // sidebar's own box instead of the viewport - rendering it docked to the
    // left at sidebar width instead of centered on the page.
    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                        {isProvisioning ? 'Setting up Workspace' : 'Create Workspace'}
                    </h2>
                    {!isProvisioning && (
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>

                {isProvisioning ? (
                    <div className="p-8 flex flex-col items-center justify-center text-center space-y-6 animate-in fade-in duration-300">
                        <div className="relative flex items-center justify-center w-20 h-20">
                            {/* Dynamic outer circular pulse */}
                            <div className="absolute inset-0 rounded-full bg-blue-50 border border-blue-100 animate-ping opacity-75"></div>
                            {/* Middle rotating ring */}
                            <div className="absolute inset-2 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin"></div>
                            {/* Inner icon frame */}
                            <div className="absolute inset-4 rounded-full bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-200">
                                <svg className="w-6 h-6 text-white animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                            </div>
                        </div>

                        <div className="space-y-2 max-w-sm">
                            <h3 className="text-base font-bold text-gray-900 tracking-tight">
                                {getStatusStepInfo().title}
                            </h3>
                            <p className="text-xs text-gray-500 leading-relaxed min-h-[32px]">
                                {getStatusStepInfo().description}
                            </p>
                        </div>

                        {/* Progress Bar */}
                        <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden relative">
                            <div 
                                className="bg-blue-600 h-full rounded-full transition-all duration-500 ease-out"
                                style={{ width: `${getStatusStepInfo().progress}%` }}
                            ></div>
                        </div>

                        {/* Stage status indicator logs */}
                        <div className="w-full text-left bg-gray-50 border border-gray-100 rounded-xl p-4 text-xs font-mono text-gray-600 space-y-2">
                            <div className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                                <span>Worker thread spawned (Job ID: {jobId?.substring(0, 8)})</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`w-1.5 h-1.5 rounded-full ${getStatusStepInfo().progress >= 25 ? 'bg-green-500' : 'bg-gray-300 animate-pulse'}`}></span>
                                <span>Validating workspace slug registry...</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`w-1.5 h-1.5 rounded-full ${getStatusStepInfo().progress >= 60 ? 'bg-green-500' : 'bg-gray-300 animate-pulse'}`}></span>
                                <span>Running PostgreSQL isolated schema migrations...</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`w-1.5 h-1.5 rounded-full ${getStatusStepInfo().progress >= 90 ? 'bg-green-500' : 'bg-gray-300 animate-pulse'}`}></span>
                                <span>Seeding default employee identities & admin RBAC...</span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="p-6">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Company Name</label>
                                <input
                                    type="text"
                                    required
                                    value={companyName}
                                    onChange={handleCompanyNameChange}
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm outline-none"
                                    placeholder="Acme Corp"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Workspace URL Slug</label>
                                <div className="flex rounded-xl border border-gray-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all text-sm overflow-hidden bg-white">
                                    <span className="bg-gray-50 px-4 py-2.5 text-gray-400 text-sm border-r border-gray-100 flex items-center select-none font-mono">
                                        {window.location.host}/
                                    </span>
                                    <input
                                        type="text"
                                        required
                                        value={slug}
                                        onChange={(e) => setSlug(parseSlugInput(e.target.value))}
                                        className="w-full px-4 py-2.5 text-sm outline-none font-mono bg-transparent"
                                        placeholder="acme-corp"
                                    />
                                </div>
                                <p className="text-xs text-gray-400 mt-1.5">This will be your dedicated company workspace identifier.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Country</label>
                                <select
                                    required
                                    value={countryCode}
                                    onChange={(e) => setCountryCode(e.target.value)}
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm outline-none bg-white font-medium text-gray-900"
                                >
                                    <option value="">Select Country</option>
                                    <option value="OM">Oman</option>
                                    <option value="AE">United Arab Emirates</option>
                                </select>
                            </div>
                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-1 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isCreating || !companyName || !slug || !countryCode}
                                    className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2 text-sm shadow-sm shadow-blue-200"
                                >
                                    {isCreating ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Queueing...
                                        </>
                                    ) : (
                                        'Create Workspace'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};
